import { createContext, useContext, useState, useEffect } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'

const AdminAuthContext = createContext()

export const useAdmin = () => {
    const context = useContext(AdminAuthContext)
    if (!context) {
        throw new Error('useAdmin must be used within AdminAuthProvider')
    }
    return context
}

export const AdminAuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null)
    const [adminData, setAdminData] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Check if user is admin in admins collection
                try {
                    const adminDoc = await getDoc(doc(db, 'admins', user.uid))
                    if (adminDoc.exists()) {
                        const data = adminDoc.data()
                        setAdminData(data)
                        setCurrentUser({
                            uid: user.uid,
                            email: user.email,
                            name: data.name || user.displayName || user.email,
                            role: data.role || 'manager', // Default role is manager
                            department: data.department,
                            phone: data.phone,
                            active: data.active !== false // Default to active if not specified
                        })
                    } else {
                        // User not in admins collection
                        setCurrentUser(null)
                        setAdminData(null)
                    }
                } catch (error) {
                    console.error('Error fetching admin data:', error)
                    setCurrentUser(null)
                    setAdminData(null)
                }
            } else {
                setCurrentUser(null)
                setAdminData(null)
            }
            setLoading(false)
        })

        return unsubscribe
    }, [])

    // Permission system
    // Admin: full permissions
    // Manager: same as admin but cannot delete anything
    // Staff: can only create/update their own content, cannot delete
    const hasPermission = (action, resource) => {
        if (!currentUser) return false

        const { role } = currentUser

        // Admin has all permissions
        if (role === 'admin') return true

        // Manager can do everything except delete
        if (role === 'manager') {
            if (action === 'delete') return false
            return true
        }

        // Staff (and other roles) can create and update but not delete
        if (action === 'delete') return false
        if (action === 'create') {
            // Staff cannot create other staff
            if (resource === 'admins') return false
            return true
        }
        if (action === 'update') return true
        if (action === 'read') return true

        return false
    }

    const value = {
        currentUser,
        adminData,
        loading,
        hasPermission,
        isAdmin: currentUser?.role === 'admin',
        isManager: currentUser?.role === 'manager',
        isStaff: currentUser && currentUser.role !== 'admin' && currentUser.role !== 'manager'
    }

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    )
}

